// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useMemo, useState } from "react";
import styles from "./styles";
import { compose, withProps } from "recompose";
import { Box, Button, Typography, withStyles, Checkbox, FormControlLabel } from "@material-ui/core";
import { connect } from "react-redux";
import { fetchCustomFieldDetails, fetchCustomFieldsOptions, updateCustomField, addCustomField, fetchCustomFieldNameUnique, deleteCustomFields } from "../../../actions/customFields"
import { getIsLoading, getCustomFieldEditData, getCustomFieldsOptionsData, getUpdateCustomFieldsData, getAddCustomFieldsData, getCustomFieldNameUnique, getIsDeleteSuccess, getErrors } from "../../../reducers/customFields"
import { AlertDialog, AlertMessages, FormActionButtons, FormButtonGroup, FormSelect, FormTextField, Loading } from "../../../components";
import { useIntl } from "react-intl";
import { getI18n } from "../../../utils/intl";
import get from "lodash/get";
import { push } from 'connected-react-router';
import clsx from "clsx";
import PropTypes, { object } from 'prop-types';
import OrderableList from "./OrderableList";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import { useHistory } from "react-router";
import { BreadcrumbsForPages } from "../../../pageComponents";
import { CUSTOM_FIELD_DETAIL } from "../../../pageComponents/BreadcrumbsForPages/constants";
import { isEmpty } from "lodash";

const CUSTOM_FIELD_TOGGLE_BUTTON_ENABLED = 'ENABLED';
const CUSTOM_FIELD_TOGGLE_BUTTON_DISABLED = 'DISABLED';
const CUSTOM_FIELD_DROPDOWN_TEXT = 'Single-Select Dropdown';
const CUSTOM_FIELD_INPUT_TEXT = 'Text';
const ENTITY_TYPE_APPLICATION = 'Applications';
const ENTITY_TYPE_API = 'Apis';

const CreatePageContent = (props) => {
  const {
    fieldName,
    handleChange,
    classes,
    customFieldsTypesOptions,
    entityTypesOptions,
    required,
    description,
    entityType,
    type,
    status,
    customFieldsStateOptions,
    handleUpdateCustomField,
    push,
    options,
    handleOptions,
    handleAddOptions,
    handleDeleteOption,
    handleAddOptionsOnKeyPress,
    getCustomFieldNameUnique,
    fieldNameEmptyError,
    handleBlurOnName,
    fieldNameFromApi,
    fieldNameFromState,
    setOptions,
    isEditFlow,
    isDisableSaveButton,
    isFormDirty,
  } = props;

  const intl = getI18n(useIntl());

  const emptyFieldName = <span className={classes.textError}>{intl.getI18nMessage("error.field.mandatory")}</span>;
  const inValidFieldName = <span className={classes.textError}>{intl.getI18nMessage("label.custom.fields.name.unique")}</span>;
  const addOptionClass = clsx(classes.cancelButton, classes.addOptionButton);

  const isModifiedAnduniqueName = getCustomFieldNameUnique === false &&
    fieldNameFromApi !== fieldNameFromState;

  const fieldNameHelperText = useMemo(() => {
    if (isModifiedAnduniqueName) {
      return inValidFieldName;
    } else if (fieldNameEmptyError) {
      return emptyFieldName;
    } else {
      return intl.getI18nMessage("label.custom.fields.add.field.name.helptext");
    }
  }, [isModifiedAnduniqueName, fieldNameEmptyError]);

  const fieldNameError = isModifiedAnduniqueName || fieldNameEmptyError;

  return (
    <Box className={classes.formSection}>
      <FormTextField
        name={intl.getI18nMessage("label.custom.fields.add.field.name")}
        helperText={fieldNameHelperText}
        maxLength="50"
        handleChange={(value) => handleChange(value, "name")}
        value={fieldName}
        handleBlur={handleBlurOnName}
        error={fieldNameError}
        id='custom-fields-field-name'
      />
      <FormControlLabel
        control={
          <Checkbox
            onChange={(e) => handleChange(e.target.checked, "required")}
            checked={required}
            value={required}
            id='custom-fields-required-field'
            data-apim-test='custom-fields-required-field'
          />
        }
        label={
          <>
            <Typography className={classes.requiredFieldLabel}>{intl.getI18nMessage("label.required.field")}</Typography>
            <Typography>{intl.getI18nMessage("label.custom.fields.add.required.field.helptext")}</Typography>
          </>
        }
        className={classes.requiredFieldContainer}
      />
      <FormTextField
        name={intl.getI18nMessage("label.custom.fields.add.field.description")}
        helperText={intl.getI18nMessage("label.settings.api.default.proxy.helptext")}
        maxLength="255"
        multiline={true}
        optional={true}
        rows={5}
        handleChange={(value) => handleChange(value, "description")}
        value={description}
        id='custom-fields-description'
      />
      <FormSelect
        name={intl.getI18nMessage("label.custom.fields.list.column.form")}
        helperText={intl.getI18nMessage("label.custom.fields.add.form.helptext")}
        defaultText={intl.getI18nMessage("label.custom.fields.add.select.default.text")}
        data={entityTypesOptions?.map((value) => ({ uuid: value, name: value }))}
        handleChange={(e) => handleChange(e.target.value, "entityType")}
        value={entityType}
        disabled={isEditFlow}
        id='custom-fields-form'
      />
      <FormSelect
        name={intl.getI18nMessage("label.custom.fields.list.column.type")}
        helperText={intl.getI18nMessage("label.custom.fields.add.form.helptext")}
        defaultText={intl.getI18nMessage("label.custom.fields.add.select.default.text")}
        data={customFieldsTypesOptions?.map((value) => ({ uuid: value, name: value }))}
        handleChange={(e) => handleChange(e.target.value, "type")}
        value={type}
        disabled={isEditFlow}
        id='custom-fields-input-type'
      />

      {
        type === CUSTOM_FIELD_DROPDOWN_TEXT ?
          <>
            <Typography variant="h4" className={classes.dropDownTitle}>{intl.getI18nMessage("label.custom.fields.add.option.title")}</Typography>
            <Typography className={classes.dropDownHelpText}>{intl.getI18nMessage("label.custom.fields.add.option.helptext")}</Typography>
            <Typography className={classes.dropDownHelpText}>{intl.getI18nMessage("label.settings.api.default.proxy.helptext")}</Typography>
            <DndProvider backend={HTML5Backend}>
              <OrderableList
                options={options}
                setOptions={setOptions}
                handleOptions={handleOptions}
                handleDeleteOption={handleDeleteOption}
                handleAddOptionsOnKeyPress={handleAddOptionsOnKeyPress}
                classes={classes}
              />
            </DndProvider>
            <Button data-apim-test='add-option-button' className={addOptionClass} variant="outlined" onClick={handleAddOptions}>{intl.getI18nMessage("label.custom.fields.add.option.text")}</Button>
          </>
          : ""
      }

      <FormButtonGroup
        name={intl.getI18nMessage("label.applicaiton.list.page.status.column")}
        data={customFieldsStateOptions}
        handleChange={(value) => handleChange(value, "status")}
        value={status}
        id='custom-fields-state'
      />
      {
        (isFormDirty || !isEditFlow) && <FormActionButtons
          onNextClick={handleUpdateCustomField}
          nextText={intl.getI18nMessage('label.save.button')}
          onCancelClick={() => push('/publish/settings/custom-fields')}
          id="custom-fields-edit-form-buttons"
          isDisabled={isDisableSaveButton}
          cancelText={intl.getI18nMessage("label.cancel.button")}
          isContainerFooter
        />
      }
    </Box>
  )
}

CreatePageContent.propTypes = {
  fieldName: PropTypes.string,
  handleChange: PropTypes.func,
  classes: PropTypes.object,
  customFieldsTypesOptions: PropTypes.array,
  entityTypesOptions: PropTypes.array,
  required: PropTypes.bool,
  description: PropTypes.string,
  entityType: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  customFieldsStateOptions: PropTypes.arrayOf(object),
  handleUpdateCustomField: PropTypes.func,
  push: PropTypes.func,
  options: PropTypes.arrayOf(object),
  handleOptions: PropTypes.func,
  handleAddOptions: PropTypes.func,
  handleDeleteOption: PropTypes.func,
  handleAddOptionsOnKeyPress: PropTypes.func,
  isDisableSaveButton: PropTypes.bool,
  getCustomFieldNameUnique: PropTypes.bool,
  fieldNameEmptyError: PropTypes.bool,
  handleBlurOnName: PropTypes.func,
  fieldNameFromApi: PropTypes.string,
  fieldNameFromState: PropTypes.string,
  setOptions: PropTypes.func,
  isEditFlow: PropTypes.bool,
  isFormDirty: PropTypes.bool,
}


export const AddOrEditCustomFieldPage = (props) => {

  const {
    isLoading,
    getCustomFieldEditData,
    fetchCustomFieldDetails,
    classes,
    customUuid,
    getCustomFieldsOptionsData,
    push,
    fetchCustomFieldsOptions,
    updateCustomField,
    getUpdateCustomFieldsData,
    fetchCustomFieldNameUnique,
    getCustomFieldNameUnique,
    deleteCustomFields,
    isDeleteSuccess,
    addCustomField,
    getAddCustomFieldsData,
    errors,
    pathName,
  } = props


  const history = useHistory();
  const isEditFlow = history.location.pathname !== "/publish/settings/custom-fields/add";
  const customFieldUuid = isEditFlow ? getCustomFieldEditData?.uuid : "";
  const [options, setOptions] = useState(isEditFlow ? [] : [{ ordinal: 1, value: "" }]);
  const [fieldNameEmptyError, setFieldNameEmptyError] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [editCustomFieldsData, setEditCustomFieldsData] = useState({
    name: "",
    description: "",
    entityType: "",
    type: "",
    status: CUSTOM_FIELD_TOGGLE_BUTTON_DISABLED,
    required: true,
  });

  const intl = getI18n(useIntl());


  const customFieldsTypesOptions = getCustomFieldsOptionsData?.customFieldTypes?.map((value) =>
    value === "SINGLE_SELECT" ? CUSTOM_FIELD_DROPDOWN_TEXT : value === "TEXT" ? CUSTOM_FIELD_INPUT_TEXT : "",
  );
  const entityTypesOptions = getCustomFieldsOptionsData?.entityTypes?.map((value) =>
    value === "API" ? ENTITY_TYPE_API : value === "APPLICATION" ? ENTITY_TYPE_APPLICATION : "",
  );

  const customFieldsStateOptions = [
    {
      name: intl.getI18nMessage("label.apis.filter.states.enabled"),
      uuid: CUSTOM_FIELD_TOGGLE_BUTTON_ENABLED,
    },
    {
      name: intl.getI18nMessage("label.apis.filter.states.disabled"),
      uuid: CUSTOM_FIELD_TOGGLE_BUTTON_DISABLED,
    },
  ];

  const handleChange = (value, fieldName) => {
    setEditCustomFieldsData({ ...editCustomFieldsData, [fieldName]: value });
  }

  useEffect(() => {
    fetchCustomFieldsOptions();
    isEditFlow && fetchCustomFieldDetails(customUuid);
  }, [customUuid])

  useEffect(() => {
    if (!isEmpty(getCustomFieldEditData)) {
      setEditCustomFieldsData({
        ...editCustomFieldsData,
        name: getCustomFieldEditData.name,
        description: getCustomFieldEditData.description,
        entityType: getCustomFieldEditData?.entityType === "API" ? ENTITY_TYPE_API : getCustomFieldEditData?.entityType === "APPLICATION" ? ENTITY_TYPE_APPLICATION : "",
        type: getCustomFieldEditData?.type === "SINGLE_SELECT" ? CUSTOM_FIELD_DROPDOWN_TEXT : getCustomFieldEditData?.type === "TEXT" ? CUSTOM_FIELD_INPUT_TEXT : "",
        status: getCustomFieldEditData.status,
        required: getCustomFieldEditData.required,
      });

      setOptions(get(getCustomFieldEditData, "options", []))
    }
  }, [getCustomFieldEditData])

  const handleAddOptions = () => {
    setOptions(options => {
      const optionId = options.reduce((acc, curr) => {
        if (curr.ordinal > acc) return curr.ordinal;
        return acc;
      }, 0) + 1;
      return [...options, { ordinal: optionId, value: '' }]
    })
  };

  const handleAddOptionsOnKeyPress = (e) => {
    if (e.key === "Enter") {
      setOptions(options => {
        const optionId = options.reduce((acc, curr) => {
          if (curr.ordinal > acc) return curr.ordinal;
          return acc;
        }, 0) + 1;
        return [...options, { ordinal: optionId, value: '' }]
      })
    }
  }

  const handleDeleteOption = (ordinal) => {
    const newInputs = options.filter((input) => input.ordinal !== ordinal);
    setOptions(newInputs);
  };

  const handleOptions = (ordinal, value) => {
    setOptions(options => {
      const option = options.find(option => option.ordinal === ordinal)
      if (!option) return options;
      option.value = value;
      return [...options]
    });
  };

  const handleUpdateCustomField = () => {
    const removeEmptyOptions = options.filter(option => option.value.trim() !== "");
    const payload = {
      name: editCustomFieldsData.name,
      description: editCustomFieldsData.description,
      entityType: editCustomFieldsData?.entityType === ENTITY_TYPE_API ? "API" :
        editCustomFieldsData?.entityType === ENTITY_TYPE_APPLICATION ? "APPLICATION" : "",
      type: editCustomFieldsData?.type === CUSTOM_FIELD_DROPDOWN_TEXT ? "SINGLE_SELECT" :
        editCustomFieldsData?.type === CUSTOM_FIELD_INPUT_TEXT ? "TEXT" : "",
      status: editCustomFieldsData.status,
      required: editCustomFieldsData.required,
      options: editCustomFieldsData?.type === CUSTOM_FIELD_DROPDOWN_TEXT ?
          removeEmptyOptions : [],
      ...(isEditFlow && { uuid : customUuid }),
    }

    isEditFlow ? updateCustomField(customUuid, payload) : addCustomField(payload);

  }

  useEffect(() => {
    if (getUpdateCustomFieldsData || getAddCustomFieldsData) {
      push('/publish/settings/custom-fields');
    }
  }, [getUpdateCustomFieldsData, getAddCustomFieldsData]);

  const isDisableSaveButton = useMemo(() => {
    if (editCustomFieldsData.name === "" || editCustomFieldsData.entityType === ""
      || editCustomFieldsData.type === "" || (getCustomFieldNameUnique === false
        && getCustomFieldEditData?.name !== editCustomFieldsData?.name) ||
      (options.every(obj => obj.value.trim() === "") && editCustomFieldsData.type === CUSTOM_FIELD_DROPDOWN_TEXT)
    ) {
      return true;
    } else {
      return false;
    }
  }, [editCustomFieldsData, options, getCustomFieldNameUnique])

  const handleBlurOnName = () => {
    if (editCustomFieldsData.name === "") {
      setFieldNameEmptyError(true)
    } else if (editCustomFieldsData.name !== "") {
      setFieldNameEmptyError(false)
      fetchCustomFieldNameUnique({ uuid : customFieldUuid, name : editCustomFieldsData.name })
    }
  }

  const onDelete = () => {
    setShowConfirmDialog(true);
  }

  const deleteConfirmDialog = () => {
    setShowConfirmDialog(false);
    deleteCustomFields(getCustomFieldEditData);
  }

  useEffect(() => {
    if (isDeleteSuccess) {
      push("/publish/settings/custom-fields");
    }
  }, [isDeleteSuccess]);

  const getAreOptionsDirty = (optionsFromState, optionsFromApi) => {
    if (optionsFromState.length !== optionsFromApi.length) {
      return true;
    }

    return optionsFromState.some((option, index) => {
      const optionFromApi = optionsFromApi[index];
      return Object.keys(option).some(key => option[key] !== optionFromApi[key]);
    });
  };



  const optionsListFromApi = get(getCustomFieldEditData, "options", []);

  const isFormDirty = useMemo(() => {
    if (getCustomFieldEditData) {
      if (getCustomFieldEditData.name !== editCustomFieldsData.name ||
        getCustomFieldEditData.description !== editCustomFieldsData.description ||
        (getCustomFieldEditData.entityType === "API" ? ENTITY_TYPE_API : getCustomFieldEditData.entityType === "APPLICATION" ? ENTITY_TYPE_APPLICATION : "") !== editCustomFieldsData.entityType ||
        (getCustomFieldEditData.type === "TEXT" ? CUSTOM_FIELD_INPUT_TEXT : getCustomFieldEditData.type === "SINGLE_SELECT" ? CUSTOM_FIELD_DROPDOWN_TEXT : "") !== editCustomFieldsData.type ||
        getCustomFieldEditData.required !== editCustomFieldsData.required ||
        getCustomFieldEditData.status !== editCustomFieldsData.status ||
        getAreOptionsDirty(options, optionsListFromApi)
      ) {
        return true;
      } else {
        return false;
      }
    }
  }, [editCustomFieldsData, getCustomFieldEditData, options, optionsListFromApi])

  useEffect(() => {
    if (errors.length > 0) {
      const validationErrors = errors && errors[0]?.error;
      let internalServerError = errors && errors[0]?.response?.data?.error?.message?.value;
      internalServerError = internalServerError || errors && errors[0]?.response?.data?.error;
      setErrorMsg(validationErrors || internalServerError);
      setShowAlert(true);
    } else {
      setShowAlert(false);
      setErrorMsg('');
    }
  }, [errors])

  const editCustomFieldTitle = intl.getI18nMessage("label.custom.fields.edit");
  const addCustomFieldTitle = intl.getI18nMessage("label.custom.fields.add");

  const breadcrumbItems = [...CUSTOM_FIELD_DETAIL,
    ...[ { isActive: false, link: '', linkName: isEditFlow ? editCustomFieldTitle : addCustomFieldTitle }]]

  return (
    <div className={classes.root}>
      {
        isLoading && <Loading pageLoader />
      }
      {showConfirmDialog && (
        <AlertDialog
          isOpen={showConfirmDialog}
          title={intl.getI18nMessage('warning.status')}
          description={intl.getI18nMessage('label.custom.field.delete.confirmation.text')}
          submitText={intl.getI18nMessage('label.delete.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={() => setShowConfirmDialog(false)}
          onSubmit={() => deleteConfirmDialog()}
          onCancel={() => setShowConfirmDialog(false)}
          submitButtonClass={classes.deleteSubmitButton}
        />
      )}
      <BreadcrumbsForPages pageContainerClass={classes.pageBreadCrumbClass}
                           pathName={pathName} items={breadcrumbItems} />
      <Box className={classes.flex}>
        <Typography data-apim-test='custom-fields-title' variant="h1">{isEditFlow ? editCustomFieldTitle : addCustomFieldTitle}</Typography>
        {
          isEditFlow &&
          <Button
            data-apim-test='delete-button'
            variant="outlined"
            className={classes.deleteButton}
            onClick={onDelete}
            disabled={getCustomFieldEditData?.status === CUSTOM_FIELD_TOGGLE_BUTTON_ENABLED
              ? true : false}
          >
            {intl.getI18nMessage("label.details.header.delete")}
          </Button>
        }
      </Box>
      <Typography data-apim-test='custom-fields-details-title' className={classes.detailsTitile} variant="h4">{intl.getI18nMessage("label.details")}</Typography>
      <CreatePageContent
        handleChange={handleChange}
        fieldName={editCustomFieldsData.name}
        classes={classes}
        customFieldsTypesOptions={customFieldsTypesOptions}
        entityTypesOptions={entityTypesOptions}
        required={editCustomFieldsData.required}
        description={editCustomFieldsData.description}
        entityType={editCustomFieldsData.entityType}
        type={editCustomFieldsData.type}
        status={editCustomFieldsData.status}
        customFieldsStateOptions={customFieldsStateOptions}
        push={push}
        handleUpdateCustomField={handleUpdateCustomField}
        options={options}
        setOptions={setOptions}
        handleOptions={handleOptions}
        handleAddOptions={handleAddOptions}
        handleDeleteOption={handleDeleteOption}
        handleAddOptionsOnKeyPress={handleAddOptionsOnKeyPress}
        handleBlurOnName={handleBlurOnName}
        fieldNameEmptyError={fieldNameEmptyError}
        getCustomFieldNameUnique={getCustomFieldNameUnique}
        fieldNameFromApi={getCustomFieldEditData?.name}
        fieldNameFromState={editCustomFieldsData?.name}
        isEditFlow={isEditFlow}
        isDisableSaveButton={isDisableSaveButton}
        isFormDirty={isFormDirty}
      />
      {
        showAlert &&
        <AlertMessages
          variant="error"
          message={errorMsg}
          onClose={() => {
            setShowAlert(false);
          }}
        />
      }
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    isLoading: getIsLoading(state),
    getCustomFieldEditData: getCustomFieldEditData(state),
    getCustomFieldsOptionsData: getCustomFieldsOptionsData(state),
    getUpdateCustomFieldsData: getUpdateCustomFieldsData(state),
    getAddCustomFieldsData: getAddCustomFieldsData(state),
    getCustomFieldNameUnique: getCustomFieldNameUnique(state),
    isDeleteSuccess: getIsDeleteSuccess(state),
    errors: getErrors(state),
    pathName: get(state, 'router.location.pathname'),
  }
}

const mapDispatchToProps = {
  fetchCustomFieldDetails,
  fetchCustomFieldsOptions,
  updateCustomField,
  addCustomField,
  fetchCustomFieldNameUnique,
  deleteCustomFields,
  push,
}

const getCustomUuid = (props) => get(props, 'match.params.uuid', '');

const addProps = (props) => ({
  customUuid: getCustomUuid(props),
});

AddOrEditCustomFieldPage.propTypes = {
  isLoading: PropTypes.bool,
  getCustomFieldEditData: PropTypes.object,
  fetchCustomFieldDetails: PropTypes.func,
  classes: PropTypes.object,
  customUuid: PropTypes.string,
  getCustomFieldsOptionsData: PropTypes.object,
  push: PropTypes.func,
  fetchCustomFieldsOptions: PropTypes.func,
  updateCustomField: PropTypes.func,
  addCustomField: PropTypes.func,
  getUpdateCustomFieldsData: PropTypes.bool,
  getAddCustomFieldsData: PropTypes.bool,
  fetchCustomFieldNameUnique: PropTypes.func,
  getCustomFieldNameUnique: PropTypes.bool,
  deleteCustomFieldsL: PropTypes.func,
  isDeleteSuccess: PropTypes.bool,
  deleteCustomFields: PropTypes.func,
  errors: PropTypes.func,
  pathName: PropTypes.string,
}

export default compose(
  withStyles(styles),
  withProps(addProps),
  connect(mapStateToProps, mapDispatchToProps),
)(AddOrEditCustomFieldPage);

